/**
 * Edge Function Client Wrapper
 * Handles authentication and calls to protected Edge Functions
 * Part of Phase 1 security fix #859
 */

import { logger } from "./logger";
import { getSupabaseSessionWithTimeout } from "./supabaseApi";
import {
  getGuestOwnerToken,
  getOwnerTokenForPoll,
} from "./guestIdentityService";
import { getEnv } from "./env";

const EDGE_FUNCTION_BASE = (() => {
  // IMPORTANT : utiliser getEnv (import.meta.env) et NON process.env.
  // Dans le bundle navigateur de prod, process.env.VITE_SUPABASE_URL est
  // undefined → fallback sur window.location.origin → les appels partent vers
  // https://doodates.com/functions/v1/... (Vercel) au lieu de Supabase → 405.
  const supabaseUrl = getEnv("VITE_SUPABASE_URL");
  if (supabaseUrl) {
    return `${supabaseUrl}/functions/v1`;
  }
  // Fallback: use client-side window.location to access current host
  if (typeof window !== "undefined") {
    return `${window.location.origin}/functions/v1`;
  }
  throw new Error("VITE_SUPABASE_URL not configured and window not available");
})();

async function getAuthToken(): Promise<string | null> {
  try {
    const session = await getSupabaseSessionWithTimeout(2000);
    return session?.access_token || null;
  } catch (error) {
    logger.warn("Failed to get auth token for Edge Function", "api", {
      error,
    });
    return null;
  }
}

async function callEdgeFunction<T>(
  functionName: string,
  body: unknown,
): Promise<T> {
  const token = await getAuthToken();
  if (!token) {
    throw new Error("Authentication required for this operation");
  }

  const url = `${EDGE_FUNCTION_BASE}/${functionName}`;
  logger.debug(`Calling Edge Function: ${functionName}`, "api", { url });

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage =
      errorData.error || `HTTP ${response.status}: ${response.statusText}`;
    logger.error(`Edge Function error: ${functionName}`, "api", {
      status: response.status,
      error: errorMessage,
    });
    throw new Error(errorMessage);
  }

  const result = await response.json();
  logger.debug(`Edge Function success: ${functionName}`, "api", { result });
  return result as T;
}

/**
 * Consume AI credit for current user
 */
export async function consumeAiCredit(): Promise<{
  success: boolean;
  credits_remaining: number;
  plan: string;
  message: string;
}> {
  return callEdgeFunction("consume-ai-credit", {});
}

/**
 * Consume credits
 */
export async function consumeCredits(amount: number): Promise<{
  success: boolean;
  message: string;
}> {
  return callEdgeFunction("consume-credits", { amount });
}

/**
 * Consume quota credits
 */
export async function consumeQuotaCredits(
  action: string,
  credits: number,
): Promise<{
  success: boolean;
  message: string;
}> {
  return callEdgeFunction("consume-quota-credits", { action, credits });
}

/**
 * Redeem beta key
 */
export async function redeemBetaKey(code: string): Promise<{
  success: boolean;
  error?: string;
  tier?: string;
  credits?: number;
  expires_at?: string;
}> {
  return callEdgeFunction("redeem-beta-key", { code });
}

/**
 * Create poll (supports both authenticated and guest creation)
 */
export async function createPoll(params: {
  type: string;
  title: string;
  slug: string;
  poll_data_json: unknown;
  description?: string;
  guest_fingerprint?: string;
  guest_session_id?: string;
  guest_owner_token?: string;
  status?: string;
  /** Provenance (défaut serveur 'web') : 'ai_link' pour le lien pré-rempli. */
  source?: "web" | "ai_link";
  source_client?: string;
  /** Langue du sondage (fr/en/es), posée dès la création. */
  language?: "fr" | "en" | "es";
}): Promise<unknown> {
  const token = await getAuthToken();
  const isGuest = params.guest_fingerprint && !token;

  if (!token && !isGuest) {
    throw new Error("Authentication required for this operation");
  }

  const url = `${EDGE_FUNCTION_BASE}/create-poll`;
  logger.debug(`Calling Edge Function: create-poll`, "api", { url, isGuest });

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  // Sécurité #828 : pour une création invité, joindre le jeton de possession
  // (256 bits, localStorage). Sans lui, le poll retombe sur le contrôle
  // fingerprint forgeable → IDOR. Le serveur n'en stocke que le hash SHA-256.
  // (Régression : le wrapper Edge #859 ne transmettait pas ce champ, si bien
  //  que 100 % des polls invités étaient créés sans jeton.)
  const body: Record<string, unknown> = { ...params };
  if (isGuest && !params.guest_owner_token) {
    const ownerToken = getGuestOwnerToken();
    if (ownerToken) body.guest_owner_token = ownerToken;
  }

  let response = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });

  // Jeton expiré/périmé : la session en cache peut être stale (long chat IA
  // avant clic "Utiliser ce sondage"). On force un refresh et on retente une
  // fois avant d'abandonner (cf pattern supabaseApi.ts).
  if (response.status === 401 && token) {
    logger.warn("create-poll 401 - Attempting session refresh", "api");
    try {
      const { supabase } = await import("./supabase");
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session?.access_token) {
        headers.Authorization = `Bearer ${session.access_token}`;
        response = await fetch(url, {
          method: "POST",
          headers,
          body: JSON.stringify(body),
        });
      }
    } catch (refreshError) {
      logger.error("create-poll session refresh failed", "api", refreshError);
    }
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage =
      errorData.error || `HTTP ${response.status}: ${response.statusText}`;
    logger.error(`Edge Function error: create-poll`, "api", {
      status: response.status,
      error: errorMessage,
    });
    throw new Error(errorMessage);
  }

  const result = await response.json();
  logger.debug(`Edge Function success: create-poll`, "api", { result });
  return result;
}

/**
 * Envoie le lien de résultats d'un sondage créé sans compte, avec le jeton
 * de possession (owner_token) — seul moyen de le retrouver depuis un autre
 * appareil. Toujours anonyme (verify_jwt=false côté fonction) : le garde-fou
 * anti-relais est in-code, pas un Bearer token.
 */
export async function sendGuestOwnerLink(params: {
  pollId: string;
  email: string;
  lang?: string;
}): Promise<{ success: boolean }> {
  // Jeton propre à CE sondage (trousseau) s'il existe, sinon principal.
  const ownerToken = getOwnerTokenForPoll(params.pollId);
  if (!ownerToken) {
    throw new Error("No guest owner token available");
  }

  const url = `${EDGE_FUNCTION_BASE}/send-guest-owner-link`;
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      pollId: params.pollId,
      email: params.email,
      ownerToken,
      lang: params.lang,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage =
      errorData.error || `HTTP ${response.status}: ${response.statusText}`;
    logger.error("Edge Function error: send-guest-owner-link", "api", {
      status: response.status,
      error: errorMessage,
    });
    throw new Error(errorMessage);
  }

  return response.json();
}

/**
 * Submit poll response
 *
 * Session présente → edge function (JWT validé côté serveur, la RPC voit
 * service_role = attestation d'authentification pour requireAuth).
 * Pas de session → appel RPC direct PostgREST (anon key). La RPC
 * `submit_poll_response` est GRANTée à anon et applique elle-même status,
 * expiry, rate-limits et les settings (auth_required / poll_full /
 * edit_forbidden — migration 20260723090000). L'ancien comportement (throw
 * "Authentication required" client-side sans session) bloquait TOUT vote
 * anonyme depuis le wrapper #859.
 */
export async function submitPollResponse(params: {
  poll_id: string;
  response_data: unknown;
  voter_name?: string;
  email?: string;
  response_type?: string;
  fingerprint?: string;
  device_id?: string;
  /**
   * Jeton du lien d'invitation (`?invite=…`), pour les sondages dont
   * settings.voteAccess vaut 'invite'. Doit traverser LES DEUX chemins : la
   * RPC est la seule à décider, et elle rejette un vote sans preuve.
   */
  invite_token?: string;
  /**
   * Sondage « sur invitation » : autorise l'edge function à attester l'email
   * vérifié du JWT. Sans ce drapeau elle ne l'envoie pas — l'argument nommé
   * n'existe pas tant que la migration n'est pas appliquée.
   */
  invite_required?: boolean;
}): Promise<unknown> {
  const token = await getAuthToken();
  if (token) {
    return callEdgeFunction("submit-poll-response", params);
  }

  // Votant anonyme : aucune session Supabase → l'Edge Function (qui exige un
  // JWT utilisateur valide) est inaccessible. On appelle la RPC publique
  // submit_poll_response en direct (anon key) — c'est le chemin durci S1 :
  // la RPC valide status/expiry, exige le fingerprint (#921) et rate-limite.
  const { supabase } = await import("./supabase");
  // PostgREST résout la fonction par l'ENSEMBLE EXACT des arguments nommés :
  // envoyer p_invite_token à une base qui n'a pas encore la migration donne un
  // PGRST202 et casse le vote. Or le front (Vercel) et les migrations
  // (db-migrate.yml) se déploient en parallèle au merge. On n'ajoute donc la
  // clé que lorsqu'il y a réellement un jeton — c'est-à-dire jamais sur un
  // sondage public, soit la quasi-totalité des votes.
  const { data, error } = await supabase.rpc("submit_poll_response", {
    p_poll_id: params.poll_id,
    p_response_data: params.response_data,
    p_voter_name: params.voter_name || null,
    p_email: params.email || null,
    p_response_type: params.response_type || "date",
    p_fingerprint: params.fingerprint || null,
    p_device_id: params.device_id || null,
    ...(params.invite_token ? { p_invite_token: params.invite_token } : {}),
  });
  if (error) {
    logger.error("RPC error: submit_poll_response (anon)", "api", {
      error: error.message,
    });
    throw new Error(error.message || "Failed to submit response");
  }
  return data;
}

/**
 * Delete poll response — RPC publique, en direct pour tous les profils.
 *
 * Plus d'edge function pour le connecté (2026-09-28) : `delete-poll-response`
 * (supprimée depuis) appelait la RPC en service_role, donc avec auth.uid() NULL — le créateur
 * connecté n'était jamais reconnu, et un votant connecté (stable_fingerprint =
 * son uid) ne pouvait pas retirer sa réponse. Le client Supabase porte déjà la
 * session : auth.uid() est alors le vrai.
 *
 * `owner_token` : jeton de possession d'un créateur invité (#828), seule
 * preuve acceptée côté serveur depuis 20260928190000 (l'empreinte ne suffit
 * plus). Clé omise si absente : PostgREST résout la fonction par l'ensemble
 * exact des arguments nommés, et front et migrations se déploient en parallèle.
 */
export async function deletePollResponse(params: {
  response_id: string;
  fingerprint?: string;
  owner_token?: string | null;
}): Promise<unknown> {
  const { supabase } = await import("./supabase");
  const { error } = await supabase.rpc("delete_poll_response", {
    p_response_id: params.response_id,
    p_fingerprint: params.fingerprint || null,
    ...(params.owner_token ? { p_owner_token: params.owner_token } : {}),
  });
  if (error) {
    logger.error("RPC error: delete_poll_response", "api", {
      error: error.message,
    });
    throw new Error(error.message || "Failed to delete response");
  }
  return null;
}

/**
 * Verify private poll access
 */
export async function verifyPrivatePollAccess(params: {
  poll_id: string;
  email?: string;
}): Promise<unknown> {
  return callEdgeFunction("verify-private-poll-access", params);
}
